import { Box, FormGroup, Typography, makeStyles } from "@material-ui/core";
import React, { FC, useEffect } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useRecoilState } from "recoil";

import { FormErrorText } from "@/components/FormErrorText";
import { RegistrationChoice } from "@/components/registration/RegistrationChoice";
import { RegistrationPhases } from "@/components/registration/RegistrationPhases";
import { useGetMasterInterestedPropertyTypes } from "@/hooks/useGetMasterData";
import { RegistrationActions, RegistrationFlowLayout as RegistrationTemplate } from "@/layouts/RegistrationFlowLayout";
import theme from "@/theme";
import { UserRegistrationsAccoutFormState } from "@/view_models/atoms";

const useStyles = makeStyles(() => ({
  twoColumn: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
    gap: 12,
    marginTop: 24,
    "& > :last-child:nth-child(odd)": { gridColumn: "1 / -1" },
    "& > button": {
      padding: 12,
      minHeight: 0,
      border: 0,
      fontSize: 12,
      fontWeight: 400,
      lineHeight: 1.5,
      [theme.breakpoints.up("md")]: { fontSize: 14 },
    },
    "& > button[aria-checked=\"true\"]": {
      border: `1.5px solid ${theme.palette.primary.main}`,
      padding: 10.5,
      fontSize: 12,
      fontWeight: 500,
    },
    "& img": { flexShrink: 0 },
  },
  secondaryHeading: {
    fontSize: 16,
    fontWeight: 700,
    lineHeight: 1.3,
    color: theme.palette.text.primary,
    textAlign: "center",
    whiteSpace: "pre-line",
    marginBottom: 24,
    [theme.breakpoints.up("md")]: { fontSize: 20 },
  },
  noticeBox: {
    padding: "8px 12px",
    borderRadius: 8,
    backgroundColor: theme.palette.semantic["status/info-bg"],
  },
  noticeText: {
    color: theme.palette.semantic["status/info"],
    fontWeight: 400,
    lineHeight: 1.5,
    fontSize: 12,
    [theme.breakpoints.up("md")]: { fontSize: 14 },
  },
}));

interface RegisterInterestPageProps {
  totalSteps: number;
  currentStep: number;
  onNext: () => void;
  onBack: () => void;
  validate: () => Promise<boolean>;
}

const RegisterInterestPage: FC<RegisterInterestPageProps> = ({ totalSteps, currentStep, onNext, onBack, validate }) => {
  const classes = useStyles();
  const {
    control,
    formState: { errors },
    getValues,
  } = useFormContext();
  const [formData] = useRecoilState(UserRegistrationsAccoutFormState);

  const { masterInterestedPropertyTypes } = useGetMasterInterestedPropertyTypes();

  // 経験の有無はエントリ画面（registration_intro）で取得済みのため、通常はここで聞かない。
  // atomが空（仮登録離脱・別端末復帰等）の場合のみ、救済として設問を表示する。
  const isExperienceAlreadyKnown =
    formData.has_investment_experience === true || formData.has_investment_experience === false;

  const handleNext = async () => {
    const isValid = await validate();
    if (isValid) {
      onNext();
    }
  };

  useEffect(() => {
    // スクロールを最上部へ
    window.scrollTo({ top: 0, behavior: "smooth" });
    // formDataが存在する場合に初期値を設定（設問を表示しない場合も送信データに含めるため必ずセットする）
    control.setValue("has_investment_experience", formData.has_investment_experience);
    control.setValue("interested_property_type_ids", formData.interested_property_type_ids ?? []);
  }, []);

  const handleCheckInterestedPropertyType = (checkedId: number) => {
    const { interested_property_type_ids: selected_ids } = getValues();
    return selected_ids?.includes(checkedId)
      ? selected_ids?.filter((id) => id !== checkedId)
      : [...(selected_ids ?? []), checkedId];
  };

  return (
    <RegistrationTemplate
      phaseTags={<RegistrationPhases completed />}
      title={isExperienceAlreadyKnown ? "興味のある項目を\n選んでください" : "今まで不動産投資のご経験はありますか？"}
      totalSteps={totalSteps}
      currentStep={currentStep}
    >
      {/* 不動産経験の有無（エントリ画面で取得済みの場合は非表示。atomが空の場合のみ救済表示） */}
      {!isExperienceAlreadyKnown && (
        <Box>
          <Controller
            name="has_investment_experience"
            control={control}
            render={({ ...field }) => (
              <FormGroup className={classes.twoColumn}>
                <RegistrationChoice
                  {...field}
                  label="経験あり"
                  error={!!errors.has_investment_experience}
                  onChange={() => field.onChange(true)}
                  checked={field.value === true}
                  checkedIcon={<img src="/icons/ds-v2-step-check.svg" alt="" aria-hidden="true" />}
                />
                <RegistrationChoice
                  {...field}
                  label="経験なし"
                  error={!!errors.has_investment_experience}
                  onChange={() => field.onChange(false)}
                  checked={field.value === false}
                  checkedIcon={<img src="/icons/ds-v2-step-check.svg" alt="" aria-hidden="true" />}
                />
              </FormGroup>
            )}
          />
          {errors && errors.has_investment_experience && (
            <FormErrorText>{errors.has_investment_experience.message as string}</FormErrorText>
          )}
        </Box>
      )}

      <Box mt={isExperienceAlreadyKnown ? 0 : 4}>
        {!isExperienceAlreadyKnown && (
          <Typography component="h2" className={classes.secondaryHeading}>
            {"興味のある項目を\n選んでください"}
          </Typography>
        )}
        <Box className={classes.noticeBox}>
          <Typography className={classes.noticeText}>マイページでいつでも変更できます</Typography>
        </Box>
        <Controller
          name="interested_property_type_ids"
          control={control}
          render={({ ...field }) => (
            <FormGroup className={classes.twoColumn}>
              {masterInterestedPropertyTypes.map((propertyType) => (
                <RegistrationChoice
                  {...field}
                  key={propertyType.id}
                  label={propertyType.name}
                  error={!!errors.interested_property_type_ids}
                  onChange={() => field.onChange(handleCheckInterestedPropertyType(propertyType.id))}
                  checked={field.value?.includes(propertyType.id) ?? false}
                  checkedIcon={<img src="/icons/ds-v2-step-check.svg" alt="" aria-hidden="true" />}
                />
              ))}
            </FormGroup>
          )}
        />
        {errors && errors.interested_property_type_ids && (
          <FormErrorText>{errors.interested_property_type_ids.message}</FormErrorText>
        )}
      </Box>
      <RegistrationActions onNext={handleNext} onBack={onBack} />
    </RegistrationTemplate>
  );
};
export default RegisterInterestPage;
